import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import api from "../../api/axois";
import styles from "./BookingDetails.module.css";
import slide1 from "../../assets/slide1.jpg";
import { getImageUrl } from "../../utils/imageUtils";
// import { toast } from "react-toastify";
import {
  FaBed,
  FaShower,
  FaSnowflake,
  FaTv,
  FaWifi,
  FaConciergeBell,
} from "react-icons/fa";

const features = [
  { icon: <FaBed />, label: "King-size bed" },
  { icon: <FaShower />, label: "Private bathroom" },
  { icon: <FaSnowflake />, label: "Air conditioning" },
  { icon: <FaWifi />, label: "Wi-Fi" },
  { icon: <FaTv />, label: "Smart TV" },
  { icon: <FaConciergeBell />, label: "24/7 Room Service" }
];

const BookingDetails = () => {
  const [booking, setBooking] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Get booking ID from URL params
  const { id: bookingId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (bookingId) {
      api.get(`/bookings/${bookingId}`).then((res) => {
        // Accept both {booking: {...}} and {...} responses
        let bookingData = res.data.booking ? res.data.booking : res.data;
        if (!bookingData || Object.keys(bookingData).length === 0) {
          throw new Error('No booking data received');
        }
        setBooking(bookingData);
        setStatus(bookingData.status);
        setLoading(false);
      }).catch((err) => {
        setError(err.response?.data?.message || err.message || 'Failed to load booking details');
        setLoading(false);
      });
    } else {
      setError('No booking ID provided');
      setLoading(false);
    }
  }, [bookingId]);

  const handleStatusChange = async (newStatus) => {
    try {
      setLoading(true);
      const response = await api.put(`/bookings/${bookingId}/status`, { status: newStatus });
      if (response.data.success && response.data.booking) {
        setStatus(response.data.booking.status);
        setBooking(prev => ({ ...prev, status: response.data.booking.status }));
      } else {
        throw new Error(response.data.message || 'Failed to update status');
      }
    } catch (err) {
      setError('Failed to update booking status');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: <FaBed />, label: "King-size bed" },
    { icon: <FaShower />, label: "Private bathroom" },
    { icon: <FaSnowflake />, label: "Air conditioning" },
    { icon: <FaWifi />, label: "Wi-Fi" },
    { icon: <FaTv />, label: "Smart TV" },
    { icon: <FaConciergeBell />, label: "24/7 Room Service" },
  ];


  if (loading) return <div className={styles.loading}>Loading...</div>;
  if (error) return <div className={styles.error}>Error: {error}</div>;
  if (!booking) return <div className={styles.error}>Booking not found</div>;

  // Defensive: fallback for missing nested fields
  const room = booking.room || {};
  const user = booking.user || null;
  const guestName = user && user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : booking.guest_name || 'Guest';
  const guestEmail = user && user.email ? user.email : booking.guest_email || '';
  const roomImages = Array.isArray(room.images) ? room.images : [];
  const mainImage = roomImages[0] || slide1;

  // Defensive: fallback for missing payment info
  const paymentDate = booking.payment_date ? new Date(booking.payment_date) : null;
  const paymentAmount = typeof booking.total_amount === 'number' ? booking.total_amount : null;
  const paymentInfo = paymentAmount !== null && paymentDate
    ? `Payment of ₦${paymentAmount.toLocaleString()} made on ${paymentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
    : 'Payment info not available';

  // Defensive: fallback for missing stay dates
  const stayDuration = booking.start_date && booking.end_date
    ? `${new Date(booking.start_date).toLocaleDateString()} - ${new Date(booking.end_date).toLocaleDateString()}`
    : 'N/A';

  // Defensive: fallback for conveyance
  const includesPickup = booking.includes_pickup;
  const conveyanceInfo = includesPickup ? (
    <div className={styles.conveyanceInfo}>
      <input type="text" value={`Date: ${booking.pickup_date ? new Date(booking.pickup_date).toLocaleDateString() : 'N/A'}`} disabled />
      <input type="text" value={`Time: ${booking.pickup_time || 'N/A'}`} disabled />
      <input type="text" value={`Phone: ${booking.pickup_phone || 'N/A'}`} disabled />
      <input type="text" value={`Airport: ${booking.pickup_airport || 'N/A'}`} disabled />
    </div>
  ) : (
    <input type="text" value="No airport pickup requested" disabled />
  );

  return (
    <div className={styles.container}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={styles.content}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        
        {/* Back Button */}
        <div style={{
          padding: '20px 24px 0',
          marginBottom: '20px'
        }}>
          <button
            onClick={() => navigate('/admin/BookingManagement')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 8px rgba(102, 126, 234, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.3)';
            }}
          >
            <span style={{ fontSize: '16px' }}>←</span>
            Back to Bookings
          </button>
        </div>
        
        <div className={styles.bookingDetailsPage}>
          <div className={styles.imageColumn}>
            <img
              src={
                booking.room.images[0]
                  ? getImageUrl(booking.room.images[0])
                  : slide1
              }
              alt="Room"
              className={styles.mainImage}
            />
            <div className={styles.thumbnailRow}>
              {booking.room.images.map((image, index) => (
                <img
                  key={index}
                  src={getImageUrl(image)}
                  alt={`Room view ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <div className={styles.infoColumn}>
            <div className={styles.guestDetails}>
              <div className={styles.avatar}>
                {(booking.user && booking.user.first_name ? booking.user.first_name : booking.guest_name || 'Guest').charAt(0)}
              </div>
              <div>
                <strong>{booking.user && booking.user.first_name && booking.user.last_name ? `${booking.user.first_name} ${booking.user.last_name}` : booking.guest_name || 'Guest'}</strong>
                <p>{booking.user && booking.user.email ? booking.user.email : booking.guest_email}</p>
              </div>
            </div>

            <div className={styles.reservationHeader}>
              <div>
            <span className={styles.roomId}>Room ID: {booking.room.id}</span>
            <p className={styles.paymentInfo}>{paymentInfo}</p>
              </div>
              <div className={styles.statusControl}>
                <label htmlFor="statusSelect" style={{ fontWeight: 500, marginRight: 8 }}>Change Status:</label>
                <select
                  id="statusSelect"
                  value={status}
                  onChange={e => handleStatusChange(e.target.value)}
                  className={styles.statusSelect}
                  disabled={loading}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="checked_in">Checked In</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                {loading && <span className={styles.statusLoading}>Updating...</span>}
              </div>
            </div>

            <h2 className={styles.roomTitle}>{booking.room.title}</h2>
            <p className={styles.price}>
              ₦{typeof booking.room.price_per_night === 'number' ? booking.room.price_per_night.toLocaleString() : 'N/A'} Per Night
            </p>

            <p className={styles.description}>{booking.room.description}</p>

            <div className={styles.grid}>
              {features.map((feature, index) => (
                <div key={index} className={styles.gridItem}>{feature.icon} {feature.label}</div>
              ))}
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Booking Details</h3>
              <div className={styles.form}>
                <div className={styles.formGroup}>
                  <label>Stay Duration</label>
                  <input 
                    type="text" 
                    value={`${new Date(booking.start_date).toLocaleDateString()} - ${new Date(booking.end_date).toLocaleDateString()}`} 
                    disabled 
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Conveyance</label>
                  {booking.includes_pickup ? (
                    <div className={styles.conveyanceInfo}>
                      <input
                        type="text"
                        value={`Date: ${new Date(booking.pickup_date).toLocaleDateString()}`}
                        disabled
                      />
                      <input
                        type="text"
                        value={`Time: ${booking.pickup_time}`}
                        disabled
                      />
                      <input
                        type="text"
                        value={`Phone: ${booking.pickup_phone}`}
                        disabled
                      />
                      <input
                        type="text"
                        value={`Airport: ${booking.pickup_airport}`}
                        disabled
                      />
                    </div>
                  ) : (
                    <input type="text" value="No airport pickup requested" disabled />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {sidebarOpen && (
        <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default BookingDetails;
